import { useState, useMemo } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Calendar,
  Download,
  Plus,
  Trash2,
  Edit,
  MoreHorizontal,
  CircleDollarSign,
  Receipt,
  PiggyBank,
  Percent,
} from 'lucide-react';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useInventory, ProfitEntry } from '@/context/InventoryContext';
import { SaveIndicator } from '@/components/ui/save-indicator';
import { StatusCard } from '@/components/dashboard/StatusCard';

// Categories for revenue and costs
const revenueCategories = [
  'Product Sales',
  'Service Revenue',
  'Consulting',
  'Licensing',
  'Other Revenue',
];

const costCategories = [
  'Materials & Components',
  'Labor',
  'Manufacturing',
  'Shipping & Logistics',
  'Equipment',
  'Overhead',
  'Marketing',
  'Other Costs',
];

export function CashflowPage() {
  const { profitEntries, setProfitEntries, saveStatus, purchaseOrders, workOrders } = useInventory();

  // Calculate total revenue from Work Orders
  const totalWORevenue = useMemo(() => {
    return workOrders.reduce((sum, wo) => sum + wo.total, 0);
  }, [workOrders]);

  // Calculate total cost from Purchase Orders
  const totalPOCost = useMemo(() => {
    return purchaseOrders.reduce((sum, po) => sum + po.total_amount, 0);
  }, [purchaseOrders]);

  // Calculate net profit and margin using WO revenue and PO cost
  const profitFromPO = useMemo(() => {
    const netProfit = totalWORevenue - totalPOCost;
    const margin = totalWORevenue > 0 ? (netProfit / totalWORevenue) * 100 : 0;
    return { netProfit, margin };
  }, [totalWORevenue, totalPOCost]);

  const [timeRange, setTimeRange] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<ProfitEntry | null>(null);

  // Form state
  const [formType, setFormType] = useState<'revenue' | 'cost'>('revenue');
  const [formCategory, setFormCategory] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formDate, setFormDate] = useState(new Date().toISOString().split('T')[0]);

  // Filter entries by time range
  const filteredEntries = useMemo(() => {
    if (timeRange === 'all') return profitEntries;

    const now = new Date();
    const months = parseInt(timeRange);
    const cutoffDate = new Date(now.getFullYear(), now.getMonth() - months, now.getDate());

    return profitEntries.filter(entry => new Date(entry.date) >= cutoffDate);
  }, [profitEntries, timeRange]);

  // Calculate totals
  const totals = useMemo(() => {
    const totalRevenue = filteredEntries
      .filter(e => e.type === 'revenue')
      .reduce((sum, e) => sum + e.amount, 0);

    const totalCosts = filteredEntries
      .filter(e => e.type === 'cost')
      .reduce((sum, e) => sum + e.amount, 0);

    const profit = totalRevenue - totalCosts;
    const profitMargin = totalRevenue > 0 ? (profit / totalRevenue) * 100 : 0;

    return { totalRevenue, totalCosts, profit, profitMargin };
  }, [filteredEntries]);

  // Reset form
  const resetForm = () => {
    setFormType('revenue');
    setFormCategory('');
    setFormDescription('');
    setFormAmount('');
    setFormDate(new Date().toISOString().split('T')[0]);
    setEditingEntry(null);
  };

  // Open dialog for new entry
  const handleAddEntry = () => {
    resetForm();
    setDialogOpen(true);
  };

  // Open dialog for editing
  const handleEditEntry = (entry: ProfitEntry) => {
    setEditingEntry(entry);
    setFormType(entry.type);
    setFormCategory(entry.category);
    setFormDescription(entry.description);
    setFormAmount(entry.amount.toString());
    setFormDate(entry.date);
    setDialogOpen(true);
  };

  // Delete entry
  const handleDeleteEntry = (id: number) => {
    setProfitEntries(prev => prev.filter(e => e.id !== id));
  };

  // Submit form
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const amount = parseFloat(formAmount);
    if (!formCategory || !formAmount || isNaN(amount) || amount <= 0) {
      return;
    }

    if (editingEntry) {
      // Update existing entry
      setProfitEntries(prev => prev.map(entry =>
        entry.id === editingEntry.id
          ? {
              ...entry,
              type: formType,
              category: formCategory,
              description: formDescription,
              amount,
              date: formDate,
            }
          : entry
      ));
    } else {
      // Create new entry
      const newEntry: ProfitEntry = {
        id: Date.now(),
        type: formType,
        category: formCategory,
        description: formDescription,
        amount,
        date: formDate,
        created_at: new Date().toISOString(),
      };
      setProfitEntries(prev => [...prev, newEntry]);
    }

    setDialogOpen(false);
    resetForm();
  };

  // Sort entries by date (newest first)
  const sortedEntries = useMemo(() => {
    return [...filteredEntries].sort((a, b) =>
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [filteredEntries]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold">Project Profit</h2>
            <SaveIndicator status={saveStatus} />
          </div>
          <p className="text-muted-foreground">
            Track project revenue, costs, and profitability
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[150px]">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="1">1 Month</SelectItem>
              <SelectItem value="3">3 Months</SelectItem>
              <SelectItem value="6">6 Months</SelectItem>
              <SelectItem value="12">12 Months</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button onClick={handleAddEntry}>
            <Plus className="h-4 w-4 mr-2" />
            Add Entry
          </Button>
        </div>
      </div>

      {/* Summary Cards - Modern Floating Design */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatusCard
          title="Total Revenue"
          value={formatCurrency(totalWORevenue)}
          subtext={`${workOrders.length} Work Orders`}
          trend={totalWORevenue > 0 ? 'up' : 'neutral'}
          type="custom"
          icon={CircleDollarSign}
          iconColor="text-green-500"
          iconBgColor="bg-green-500/10"
          gradientColor="green"
        />

        <StatusCard
          title="Total Cost"
          value={formatCurrency(totalPOCost)}
          subtext={`${purchaseOrders.length} Purchase Orders`}
          trend={totalPOCost > 0 ? 'down' : 'neutral'}
          type="custom"
          icon={Receipt}
          iconColor="text-red-500"
          iconBgColor="bg-red-500/10"
          gradientColor="red"
        />

        <StatusCard
          title="Net Profit"
          value={formatCurrency(profitFromPO.netProfit)}
          subtext={profitFromPO.netProfit >= 0 ? 'Profitable' : 'At Loss'}
          trend={profitFromPO.netProfit >= 0 ? 'up' : 'down'}
          type="custom"
          icon={PiggyBank}
          iconColor="text-emerald-500"
          iconBgColor="bg-emerald-500/10"
          gradientColor="emerald"
        />

        <StatusCard
          title="Profit Margin"
          value={`${profitFromPO.margin.toFixed(1)}%`}
          subtext={profitFromPO.margin >= 15 ? 'Healthy' : profitFromPO.margin >= 0 ? 'Moderate' : 'Negative'}
          trend={profitFromPO.margin >= 15 ? 'up' : profitFromPO.margin >= 0 ? 'neutral' : 'down'}
          type="custom"
          icon={Percent}
          iconColor="text-blue-500"
          iconBgColor="bg-blue-500/10"
          gradientColor="blue"
        />
      </div>

      {/* Entries Table */}
      <Card>
        <CardHeader>
          <CardTitle>Entries</CardTitle>
        </CardHeader>
        <CardContent>
          {sortedEntries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <span className="text-3xl font-bold text-muted-foreground">₪</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">No entries yet</h3>
              <p className="text-muted-foreground text-center max-w-md mb-4">
                Start tracking your project profitability by adding revenue and cost entries.
              </p>
              <Button onClick={handleAddEntry}>
                <Plus className="h-4 w-4 mr-2" />
                Add Entry
              </Button>
            </div>
          ) : (
            <div className="rounded-lg border">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-3 text-left text-sm font-medium">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Category</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Description</th>
                    <th className="px-4 py-3 text-right text-sm font-medium">Amount</th>
                    <th className="px-4 py-3 text-center text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedEntries.map((entry) => (
                    <tr key={entry.id} className="border-b hover:bg-muted/30">
                      <td className="px-4 py-3 text-sm">{formatDate(entry.date)}</td>
                      <td className="px-4 py-3 text-sm">
                        <Badge variant={entry.type === 'revenue' ? 'success' : 'destructive'}>
                          {entry.type === 'revenue' ? 'Revenue' : 'Cost'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">{entry.category}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground max-w-[200px] truncate">
                        {entry.description || '-'}
                      </td>
                      <td className={cn(
                        "px-4 py-3 text-sm text-right font-bold",
                        entry.type === 'revenue' ? 'text-green-600' : 'text-red-600'
                      )}>
                        {entry.type === 'revenue' ? '+' : '-'}{formatCurrency(entry.amount)}
                      </td>
                      <td className="px-4 py-3 text-sm text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditEntry(entry)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleDeleteEntry(entry.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-muted/30">
                    <td colSpan={4} className="px-4 py-3 text-sm font-semibold text-right">
                      Net Total:
                    </td>
                    <td className={cn(
                      "px-4 py-3 text-sm text-right font-bold",
                      totals.profit >= 0 ? 'text-green-600' : 'text-red-600'
                    )}>
                      {formatCurrency(totals.profit)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Entry Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? 'Edit Entry' : 'Add New Entry'}
            </DialogTitle>
            <DialogDescription>
              {editingEntry
                ? 'Update the details of this entry.'
                : 'Add a new revenue or cost entry to track project profitability.'
              }
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Entry Type */}
            <div className="space-y-2">
              <Label>Entry Type *</Label>
              <div className="grid grid-cols-2 gap-4">
                <Button
                  type="button"
                  variant={formType === 'revenue' ? 'default' : 'outline'}
                  className={cn(
                    "w-full",
                    formType === 'revenue' && 'bg-green-600 hover:bg-green-700'
                  )}
                  onClick={() => {
                    setFormType('revenue');
                    setFormCategory('');
                  }}
                >
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Revenue
                </Button>
                <Button
                  type="button"
                  variant={formType === 'cost' ? 'default' : 'outline'}
                  className={cn(
                    "w-full",
                    formType === 'cost' && 'bg-red-600 hover:bg-red-700'
                  )}
                  onClick={() => {
                    setFormType('cost');
                    setFormCategory('');
                  }}
                >
                  <TrendingDown className="h-4 w-4 mr-2" />
                  Cost
                </Button>
              </div>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={formCategory} onValueChange={setFormCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {(formType === 'revenue' ? revenueCategories : costCategories).map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Amount and Date */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount (ILS) *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    ₪
                  </span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={formAmount}
                    onChange={(e) => setFormAmount(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formDate}
                  onChange={(e) => setFormDate(e.target.value)}
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Add a description (optional)"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!formCategory || !formAmount || parseFloat(formAmount) <= 0}
                className={formType === 'revenue' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              >
                {editingEntry ? 'Update Entry' : 'Add Entry'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
